import { AnimatedSection } from '@denqfabrik/landing-ui';
import { useTranslation } from '../../i18n';

const clientLogos = [
  // Row 1
  { name: 'Einhell', logo: '/images/clients/einhell-logo.png', url: 'https://www.einhell.de' },
  {
    name: 'Albatros',
    logo: '/images/clients/Albatros-Logo.svg',
    url: 'https://albatros-international.eu',
  },
  {
    name: 'Alpenmärchen',
    logo: '/images/clients/alpenmaerchen-logo.svg',
    url: 'https://www.alpenmaerchen.de',
  },
  {
    name: 'Riess Ambiente',
    logo: '/images/clients/riess-ambiente-logo.svg',
    url: 'https://www.riess-ambiente.de',
  },
  {
    name: 'Playflip',
    logo: '/images/clients/Playflip_Logo_2022.png',
    url: 'https://www.playflip.de',
  },
  // Row 2
  {
    name: 'Bundesbrand',
    logo: '/images/clients/bundesbrand-logo-black.png',
    url: 'https://www.bundesbrand.de',
  },
  { name: 'Selfio', logo: '/images/clients/selfio-logo.svg', url: 'https://www.selfio.de' },
  {
    name: 'Hut Online',
    logo: '/images/clients/hut-online-logo.webp',
    url: 'https://www.hut-online.de',
  },
  {
    name: 'Korbmayer',
    logo: '/images/clients/logo-Korbmayer.webp',
    url: 'https://www.korbmayer.de',
  },
  { name: 'BRZ', logo: '/images/clients/logo-brz.webp', url: 'https://www.brz.eu' },
  // Row 3
  {
    name: 'BB Versandlogistik',
    logo: '/images/clients/bb-versandlogistik-logo.png',
    url: 'https://www.bb-versandlogistik.de',
  },
  { name: 'Par71', logo: '/images/clients/par71_logo.png', url: 'https://www.par71.de' },
  { name: 'Snocks', logo: '/images/clients/snocks-logo.svg', url: 'https://www.snocks.com' },
  {
    name: 'Feldmann-Wohnen',
    logo: '/images/clients/feldmann-wohnen-logo.png',
    url: 'https://www.feldmann-wohnen.de',
  },
  {
    name: 'BIG Autobatterien',
    logo: '/images/clients/big-autobatterien-logo.svg',
    url: 'https://www.batterie-industrie-germany.de',
  },
];

export default function TrustSection() {
  const { t } = useTranslation();

  return (
    <section id="trust" className="relative z-20 bg-[#f2f5fa] pb-20 pt-20 md:pb-28 md:pt-28">
      {/* Heading — constrained width */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="mx-auto mb-6 max-w-3xl text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">
              {t.trust.subtitle}
            </p>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
              {t.trust.headingPrefix}{' '}
              <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                Denqfabrik
              </span>
            </h2>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={80}>
          <div className="mb-16 flex justify-center">
            <a
              href="#testimonials-grid"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:bg-slate-800 hover:shadow-xl"
            >
              {t.trust.ctaButton}
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9L12 15L18 9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </AnimatedSection>
      </div>

      {/* Logo Grid — full-width, edge-to-edge */}
      <AnimatedSection delay={160}>
        <div className="px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {clientLogos.map((client, i) => (
              <a
                key={`${client.name}-${i}`}
                href={client.url}
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex h-[76px] items-center justify-center rounded-xl border border-[#dce3ed] bg-[#e8edf4] px-6 transition-all duration-200 hover:border-[#c8d1de] hover:bg-[#dfe6ef]"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="relative max-h-8 max-w-[120px] object-contain opacity-50 grayscale"
                  style={{ mixBlendMode: 'multiply' }}
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Testimonial Cards — constrained width */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div id="testimonials-grid" className="mt-20 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {t.trust.testimonials.map((testimonial, index) => (
            <AnimatedSection key={testimonial.author} delay={(index + 2) * 120}>
              <article className="flex h-full flex-col justify-between rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-8">
                <blockquote className="text-[0.94rem] leading-relaxed text-slate-700">
                  &ldquo;{testimonial.quote}&rdquo;
                </blockquote>
                <div className="mt-6 border-t border-[#dce2eb] pt-5">
                  <p className="text-sm font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </article>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
